import { addDisplayName, addProps, addState, flowMax } from 'ad-hok'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { UserListPublic } from 'shared/src/api/types'
import cx from 'classnames'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { sansSerifFontFamily, serifFontFamily } from 'utils/styles'
import { LinkOrButton } from 'components/LinkOrButton'
import { makeFluidCreatorUrl } from 'utils/routes'
import { CREATOR_AVATAR_Z_INDEX } from 'utils/zIndex'
import { Creator } from 'components/Creator'
import UserBadge from 'components/profile/UserBadge'
import { Banner } from 'components/banners/Banner'
import { GO2APP_URL } from 'utils/urls'

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    justifyContent: 'center',
    position: 'relative',
  },
  titleText: {
    color: '#FFF',
    fontSize: 32,
    fontWeight: 700,
    fontFamily: serifFontFamily,
    lineHeight: '129.5%',
    letterSpacing: 0,
    marginLeft: 32,
    marginBottom: 38,
  },
  footerContainerHolder: {
    background: 'linear-gradient(transparent, #00000059 10%, black)',
    width: '100%',
    bottom: '5px',
    zIndex: '1',
    position: 'absolute',
    alignItems: 'center',
    flexDirection: 'row',
    paddingTop: '30px',
  },
  footerContainer: {
    paddingLeft: 12,
    paddingBottom: '30px',
    marginRight: 96,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    zIndex: CREATOR_AVATAR_Z_INDEX,
  },
  footerText: {
    paddingLeft: 12,
    fontFamily: sansSerifFontFamily,
    fontWeight: 600,
    color: 'white',
  },
  username: {
    fontSize: 14,
    display: '-webkit-box',
    '-webkit-line-clamp': 1,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
  },
  imageContainer: {
    border: 'none',
    backgroundColor: 'transparent',
    position: 'relative',
    width: '100%',
    maxHeight: '100%',
    display: 'flex',
    justifyContent: 'center',
    pointerEvents: 'auto',
    paddingLeft: 0,
    paddingRight: 0,
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    margin: 0,
    opacity: '100%',
    transition: 'width 0.4s ease-in-out, opacity 0.4s ease-in-out',
  },
  gradient: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    background:
      'linear-gradient(black, transparent 7%, transparent 93%, black)',
    pointerEvents: 'none',
  },
})

interface Props {
  edit: UserListPublic
  className?: string
  onClickCreator?: () => void
}

export const EditFeedItem: FC<Props> = flowMax(
  addDisplayName('EditFeedItem'),
  addTranslationHelpers,
  addProps(() => ({
    classes: useStyles(),
  })),
  addState('showBanner', 'setShowBanner', () => false),
  ({
    edit,
    onClickCreator,
    classes,
    className,
    t,
    showBanner,
    setShowBanner,
  }) => (
    <div className={cx(classes.container, className)}>
      <LinkOrButton
        onClick={() => {
          setShowBanner(true)
        }}
        showBackButton
        className={classes.imageContainer}
      >
        <img
          src={edit.coverImage?.full}
          className={classes.image}
          alt={`${edit.name} by ${edit.user.username}`}
        />
        <div className={classes.gradient} />
      </LinkOrButton>
      <div className={classes.footerContainerHolder}>
        <div className={classes.footerContainer}>
          <Creator
            creator={edit.user}
            height={40}
            shadow
            onClick={onClickCreator}
          />
          <div className={classes.footerText}>
            <LinkOrButton
              navigateTo={makeFluidCreatorUrl(edit.user.username)}
              onClick={onClickCreator}
              showBackButton
              replace={!!onClickCreator}
              className={classes.username}
            >
              <>
                {edit.user.username}
                <UserBadge
                  statusLevel={edit.user.statusLevel}
                  style={{ position: 'absolute' }}
                />
              </>
            </LinkOrButton>
          </div>
        </div>
      </div>
      {showBanner && (
        <Banner
          banner={{
            unDismissable: true,
            noHider: true,
            accessibleMenu: true,
            textTranslationKey: 'banners.mobileOpenDownloadApp.appName',
            onCloseAnalyticsEvent: {
              options: {
                category: 'general',
                action: 'banner_open_download_in_app_close',
              },
            },
            link: {
              title: t('banners.mobileOpenDownloadApp.exclusiveTitle'),
              navigateTo: window.location.href.replace(
                `${window.location.protocol}//${window.location.hostname}${
                  window.location.port ? ':' + window.location.port : ''
                }`,
                GO2APP_URL.replace(/\/$/, '')
              ),
              external: false,
              onClickAnalyticsEvent: {
                options: {
                  category: 'general',
                  action: 'banner_open_download_in_app_click',
                },
              },
            },
          }}
          onAction={() => {}}
        />
      )}
    </div>
  )
)
